import axios from 'axios';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

// Define the types for the maintenance status response
export interface MaintenanceSession {
  id: string;
  type: 'global' | 'service';
  cluster: string;
  services: string[];
  enabled: boolean;
  start_time: string;
  end_time: string;
  created_at: string;
  created_by: string;
  reason: string;
  metadata?: Record<string, any>;
  timestamp: string;
}

export interface MaintenanceStatusResponse {
  global_maintenance: MaintenanceSession | null;
  service_maintenance: Record<string, MaintenanceSession>;
  active_sessions: MaintenanceSession[];
  cluster: string;
  timestamp: string;
}

// Define the state interface
interface MaintenanceState {
  globalMaintenance: MaintenanceSession | null;
  serviceMaintenance: Record<string, MaintenanceSession>;
  activeSessions: MaintenanceSession[];
  cluster: string;
  timestamp: string;
  loading: boolean;
  error: string | null;
}

const initialState: MaintenanceState = {
  globalMaintenance: null,
  serviceMaintenance: {},
  activeSessions: [],
  cluster: '',
  timestamp: '',
  loading: false,
  error: null,
};

// Set the API base URLs
const MAINTENANCE_STATUS_API_URL = 'https://api.status.gridbit.live/api/v1/maintenance/status';
const MAINTENANCE_UPDATE_API_URL = 'https://api.status.gridbit.live/api/v1/maintenance';

// Create async thunk for fetching maintenance status
// Define the interface for the maintenance update request
export interface MaintenanceUpdateRequest {
  enabled: boolean;
  type: 'service' | 'global';
  cluster: string;
  services?: string[];
  start_time?: string | null;
  end_time?: string | null;
  reason?: string;
}

export const fetchMaintenanceStatus = createAsyncThunk(
  'maintenance/fetchStatus',
  async (cluster: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${MAINTENANCE_STATUS_API_URL}/${cluster}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching maintenance status:', error);
      return rejectWithValue(error.response?.data || error.message || 'Failed to fetch maintenance status');
    }
  }
);

// Create async thunk for updating maintenance
export const updateMaintenance = createAsyncThunk(
  'maintenance/updateMaintenance',
  async (request: MaintenanceUpdateRequest, { rejectWithValue, dispatch }) => {
    try {
      // Simple clean approach using axios with the proxy path
      const response = await axios.post(MAINTENANCE_UPDATE_API_URL, request, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      // Refresh the maintenance status after update
      await dispatch(fetchMaintenanceStatus(request.cluster));
      
      return response.data;
    } catch (error: any) {
      console.error('Error updating maintenance:', error);
      return rejectWithValue(error.response?.data || error.message || 'Failed to update maintenance');
    }
  }
);

// Create async thunk for deleting maintenance
export const deleteMaintenance = createAsyncThunk(
  'maintenance/deleteMaintenance',
  async ({ id, cluster }: { id: string, cluster: string }, { rejectWithValue, dispatch }) => {
    try {
      // DELETE request to remove the maintenance session
      const response = await axios.delete(`${MAINTENANCE_UPDATE_API_URL}/${id}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Refresh the maintenance status after deletion
      await dispatch(fetchMaintenanceStatus(cluster));
      
      return response.data;
    } catch (error: any) {
      console.error('Error deleting maintenance:', error);
      return rejectWithValue(error.response?.data || error.message || 'Failed to delete maintenance');
    }
  }
);

// Create the maintenance slice
export const maintenanceSlice = createSlice({
  name: 'maintenance',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handle fetchMaintenanceStatus
      .addCase(fetchMaintenanceStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceStatus.fulfilled, (state, action: PayloadAction<MaintenanceStatusResponse>) => {
        state.loading = false;
        state.globalMaintenance = action.payload.global_maintenance;
        state.serviceMaintenance = action.payload.service_maintenance;
        state.activeSessions = action.payload.active_sessions;
        state.cluster = action.payload.cluster;
        state.timestamp = action.payload.timestamp;
      })
      .addCase(fetchMaintenanceStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle updateMaintenance
      .addCase(updateMaintenance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMaintenance.fulfilled, (state) => {
        state.loading = false;
        toast.success('Service maintenance updated successfully');
      })
      .addCase(updateMaintenance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to update service maintenance');
      })
      
      // Handle deleteMaintenance
      .addCase(deleteMaintenance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMaintenance.fulfilled, (state) => {
        state.loading = false;
        toast.success('Service maintenance deleted successfully');
      })
      .addCase(deleteMaintenance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to delete service maintenance');
      });
  },
});

export default maintenanceSlice.reducer;
